import { useState, useEffect } from "react";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import { ReportError, ReportLoading } from "@/components/leasing/ReportState";
import Pagination from "@/components/Pagination";
import { fmtNumber } from "@/lib/format";
import { Download, Loader2, Search, Check } from "lucide-react";
import { toast } from "sonner";

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function parseRoNumber(s) {
  if (s === null || s === undefined || s === "") return null;
  let t = String(s).replace(/\s/g, "");
  const hasComma = t.includes(","), hasDot = t.includes(".");
  if (hasComma && hasDot) {
    t = t.lastIndexOf(",") > t.lastIndexOf(".") ? t.replace(/\./g, "").replace(",", ".") : t.replace(/,/g, "");
  } else if (hasComma) t = t.replace(",", ".");
  const n = parseFloat(t);
  return isNaN(n) ? null : n;
}

export default function ReportF1() {
  const [month, setMonth] = useState(currentYm());
  const [bonusText, setBonusText] = useState("");
  const [appliedBonus, setAppliedBonus] = useState(null);
  const [tab, setTab] = useState("report");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // load stored bonus when month changes
  useEffect(() => {
    let active = true;
    api.get("/fuel/bonus", { params: { month: `${month}` } }).then((r) => {
      if (!active) return;
      const amt = r.data.amount;
      setBonusText(amt != null ? String(amt) : "");
      setAppliedBonus(amt != null ? amt : 0);
      setPage(1);
    });
    return () => { active = false; };
  }, [month]);

  const { loading, error, data, reload } = useAsyncData(async () => {
    if (appliedBonus === null) return null;
    const res = await api.get("/fuel/report/f1", { params: { month: `${month}`, bonus: appliedBonus, search, page, page_size: pageSize } });
    return res.data;
  }, [month, appliedBonus, search, page]);

  const applyBonus = () => {
    const n = parseRoNumber(bonusText);
    if (n === null) { toast.error("Bonus invalid"); return; }
    setPage(1);
    setAppliedBonus(n);
    toast.success("Bonus aplicat și salvat");
  };

  const doExport = async () => {
    try { await downloadFile(`/fuel/report/f1/export?month=${month}`, `Raport_F1_${month}.xlsx`); }
    catch (e) { toast.error("Export eșuat"); }
  };

  const rows = data?.rows || [];
  const t = data?.totals;
  const warnCount = (data?.unknown_products?.length || 0) + (data?.not_found?.length || 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Lună (LL-AAAA)</Label>
          <MonthInput value={month} onChange={setMonth} className="w-40" data-testid="f1-month" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Bonus lunar</Label>
          <div className="flex items-center gap-1">
            <Input data-testid="f1-bonus-input" value={bonusText} onChange={(e) => setBonusText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyBonus()} className="w-36" placeholder="0,00" />
            <Button size="sm" variant="outline" onClick={applyBonus} className="gap-1" data-testid="f1-apply-bonus-btn"><Check className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Caută CC</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="f1-search-input" placeholder="Centru de cost…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 w-44" />
          </div>
        </div>
        {loading && <span className="flex items-center gap-1 text-xs text-slate-400 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> se calculează…</span>}
        <Button onClick={doExport} variant="outline" className="gap-2 ml-auto" data-testid="f1-export-btn"><Download className="h-4 w-4" /> Export Excel</Button>
      </div>

      {t && !error && (
        <div className="flex flex-wrap gap-4 text-sm bg-slate-900 text-white rounded p-3" data-testid="f1-totals">
          <span>Tranzacții: <b className="tabular">{t.tx_count}</b></span>
          <span>Total net: <b className="tabular">{fmtNumber(t.total_net)}</b></span>
          <span>Total 60221: <b className="tabular text-[#FFC800]">{fmtNumber(t.acc_60221)}</b></span>
          <span>Total 63501: <b className="tabular">{fmtNumber(t.acc_63501)}</b></span>
          <span>Rată bonus: <b className="tabular">{fmtNumber(t.rate, 9)}</b></span>
          <span>Total bonus: <b className="tabular">{fmtNumber(t.bonus)}</b></span>
          <span>Total 60221 FINAL: <b className="tabular text-[#FFC800]">{fmtNumber(t.final_60221)}</b></span>
        </div>
      )}

      <div className="flex gap-1">
        <button onClick={() => setTab("report")} data-testid="f1-tab-report" className={`px-3 py-1.5 text-xs font-semibold rounded ${tab === "report" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Distribuție CC</button>
        <button onClick={() => setTab("warnings")} data-testid="f1-tab-warnings" className={`px-3 py-1.5 text-xs font-semibold rounded ${tab === "warnings" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Avertismente {data ? `(${warnCount})` : ""}
        </button>
      </div>

      {error ? <ReportError onRetry={reload} /> : loading && !data ? <ReportLoading /> : tab === "report" ? (
        <>
          <div className="border rounded-md overflow-auto max-h-[58vh]">
            <table className="w-full text-sm jh-table" data-testid="report-f1-table">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2">CC</th>
                  <th className="text-right px-3 py-2">60221</th>
                  <th className="text-right px-3 py-2">63501</th>
                  <th className="text-right px-3 py-2">Bonus</th>
                  <th className="text-right px-3 py-2">60221 FINAL</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.cc} className="hover:bg-amber-50/40" data-testid={`f1-row-${r.cc}`}>
                    <td className="px-3 py-1.5 font-medium font-mono">{r.cc}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.acc_60221)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.acc_63501)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.bonus)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{fmtNumber(r.final_60221)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nicio tranzacție de repartizat pentru această lună.</td></tr>}
              </tbody>
              {t && (
                <tfoot className="sticky bottom-0 bg-slate-100 font-semibold" data-testid="f1-totals-row">
                  <tr>
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right tabular">{fmtNumber(t.acc_60221)}</td>
                    <td className="px-3 py-2 text-right tabular">{fmtNumber(t.acc_63501)}</td>
                    <td className="px-3 py-2 text-right tabular">{fmtNumber(t.bonus)}</td>
                    <td className="px-3 py-2 text-right tabular">{fmtNumber(t.final_60221)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data?.total || 0} onPage={setPage} label="centre de cost" testid="f1-pagination" />
        </>
      ) : (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold mb-2">Produse necunoscute (excluse din raport)</h3>
            <div className="border rounded-md overflow-auto max-h-[28vh]">
              <table className="w-full text-sm jh-table" data-testid="f1-unknown-products-table">
                <thead className="sticky top-0 bg-slate-100"><tr><th className="text-left px-3 py-2">Nr înmatriculare</th><th className="text-left px-3 py-2">Produs</th><th className="text-right px-3 py-2">Valoare netă</th></tr></thead>
                <tbody>
                  {(data?.unknown_products || []).map((u, i) => (
                    <tr key={i} className="hover:bg-slate-50"><td className="px-3 py-1.5">{u.plate_raw}</td><td className="px-3 py-1.5">{u.produs}</td><td className="px-3 py-1.5 text-right tabular">{fmtNumber(u.valoare_neta)}</td></tr>
                  ))}
                  {(!data || data.unknown_products.length === 0) && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Niciun produs necunoscut.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-2">Număr negăsit (plăcuțe absente din registrul de mașini)</h3>
            <div className="border rounded-md overflow-auto max-h-[28vh]">
              <table className="w-full text-sm jh-table" data-testid="f1-notfound-table">
                <thead className="sticky top-0 bg-slate-100"><tr><th className="text-left px-3 py-2">Nr înmatriculare</th><th className="text-right px-3 py-2">Valoare netă</th></tr></thead>
                <tbody>
                  {(data?.not_found || []).map((u, i) => (
                    <tr key={i} className="hover:bg-slate-50"><td className="px-3 py-1.5">{u.plate_raw}</td><td className="px-3 py-1.5 text-right tabular">{fmtNumber(u.valoare_neta)}</td></tr>
                  ))}
                  {(!data || data.not_found.length === 0) && <tr><td colSpan={2} className="px-4 py-6 text-center text-slate-400">Nicio plăcuță negăsită.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
